import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import apiService from '../services/apiService'

function AddCourse({ onClose }) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm()
  const api = apiService()
  const [selectedThumbnail, setSelectedThumbnail] = useState(null)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [isUploading, setIsUploading] = useState(false)

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']
      if (!validTypes.includes(file.type)) {
        toast.error('Invalid file type. Please select an image file (JPEG, PNG, GIF, WEBP).')
        e.target.value = ''
        return
      }
      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size too large. Maximum size is 10MB.')
        e.target.value = ''
        return
      }
      setSelectedThumbnail(file)
    }
  }

  const onSubmit = async (values) => {
    try {
      // First, create the course
      const response = await api.post('courses', {
        title: values.title,
        description: values.description || '',
        thumbnail: values.thumbnail || '', // Keep for external URLs
        status: values.status || 'draft',
      })

      if (response.status === 200 || response.status === 201) {
        const courseId = response.data.course?._id || response.data.course?.id
        
        // If a thumbnail file is selected, upload it
        if (selectedThumbnail && courseId) {
          setIsUploading(true)
          setUploadProgress(0)
          
          const formData = new FormData()
          formData.append('thumbnail', selectedThumbnail)

          try {
            await api.uploadFile(
              `courses/${courseId}/thumbnail`,
              formData,
              (progress) => setUploadProgress(progress)
            )
            toast.success('Course and thumbnail created successfully')
          } catch (uploadError) {
            console.error('Error uploading thumbnail:', uploadError)
            toast.warning('Course created but thumbnail upload failed. You can upload it later.')
          } finally {
            setIsUploading(false)
            setUploadProgress(0)
          }
        } else {
          toast.success('Course created successfully')
        }
        
        if (onClose) onClose()
      }
    } catch (error) {
      console.error('Error creating course:', error)
      toast.error('Failed to create course')
    }
  }

  return (
    <form
      className="w-full mx-auto p-0"
      onSubmit={handleSubmit(onSubmit)}
      autoComplete="off"
    >
      <div className="grid grid-cols-1 gap-4 mb-4">
        <div>
          <label className="block text-[#030303] font-medium mb-2">
            Course Title<span className="text-[#DE3C7D]">*</span>
          </label>
          <input
            type="text"
            className="w-full bg-[#FAFAFA] border-none rounded-xl px-4 py-2.5 text-[#030303] font-medium focus:ring-2 focus:ring-[#DE3C7D] outline-none"
            {...register('title', { required: 'Course title is required' })}
            placeholder="Enter course title"
          />
          {errors.title && (
            <p className="text-[#DE3C7D] text-xs mt-1">
              {errors.title.message}
            </p>
          )}
        </div>
        <div>
          <label className="block text-[#030303] font-medium mb-2">
            Description
          </label>
          <textarea
            rows={3}
            className="w-full bg-[#FAFAFA] border-none rounded-xl px-4 py-2.5 text-[#030303] font-medium focus:ring-2 focus:ring-[#DE3C7D] outline-none resize-none"
            {...register('description')}
            placeholder="Enter course description"
          />
        </div>
        <div>
          <label className="block text-[#030303] font-medium mb-2">
            Thumbnail
          </label>
          <div className="space-y-2">
            <div>
              <label className="block text-sm text-[#535862] mb-1">
                Upload Thumbnail Image (JPEG, PNG, GIF, WEBP - Max 10MB)
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleThumbnailChange}
                className="w-full bg-[#FAFAFA] border-none rounded-xl px-4 py-2.5 text-[#030303] font-medium focus:ring-2 focus:ring-[#DE3C7D] outline-none file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#DE3C7D] file:text-white hover:file:bg-[#c8356f] cursor-pointer"
              />
              {selectedThumbnail && (
                <div className="mt-2">
                  <img
                    src={URL.createObjectURL(selectedThumbnail)}
                    alt="Preview"
                    className="w-full h-32 object-cover rounded-lg border border-[#F0F0F0]"
                  />
                  <p className="text-xs text-[#535862] mt-1">
                    Selected: {selectedThumbnail.name} ({(selectedThumbnail.size / (1024 * 1024)).toFixed(2)} MB)
                  </p>
                </div>
              )}
              {isUploading && (
                <div className="mt-2">
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div
                      className="bg-[#DE3C7D] h-2.5 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <p className="text-sm text-[#535862] mt-1">Uploading: {uploadProgress}%</p>
                </div>
              )}
            </div>
            <div className="text-center text-xs text-[#535862] py-1">OR</div>
            <div>
              <label className="block text-sm text-[#535862] mb-1">
                Enter Thumbnail URL (External Link)
              </label>
              <input
                type="url"
                className="w-full bg-[#FAFAFA] border-none rounded-xl px-4 py-2.5 text-[#030303] font-medium focus:ring-2 focus:ring-[#DE3C7D] outline-none"
                {...register('thumbnail')}
                placeholder="https://example.com/image.jpg"
                disabled={!!selectedThumbnail}
              />
            </div>
          </div>
        </div>
        <div>
          <label className="block text-[#030303] font-medium mb-2">
            Status<span className="text-[#DE3C7D]">*</span>
          </label>
          <div className="relative">
            <select
              className="w-full bg-[#FAFAFA] border-none rounded-xl px-4 py-2.5 pr-10 text-[#030303] font-medium focus:ring-2 focus:ring-[#DE3C7D] outline-none appearance-none cursor-pointer transition-all duration-200 hover:bg-[#F5F5F5]"
              {...register('status', { required: 'Status is required' })}
              defaultValue="draft"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23535862' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 16px center',
                backgroundSize: '12px',
                paddingRight: '40px'
              }}
            >
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          {errors.status && (
            <p className="text-[#DE3C7D] text-xs mt-1">
              {errors.status.message}
            </p>
          )}
        </div>
      </div>
      <div className="flex flex-col md:flex-row gap-3 mt-6">
        <button
          type="button"
          onClick={onClose}
          className="w-full md:w-1/2 px-4 py-3 rounded-xl border border-[#E0E0E0] text-[#030303] bg-white font-medium hover:bg-neutral-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="w-full md:w-1/2 px-4 py-3 rounded-xl bg-[#DE3C7D] text-white font-medium hover:bg-[#c8356f] transition-colors"
        >
          Create Course
        </button>
      </div>
    </form>
  )
}

export default AddCourse

